// @ts-nocheck
import { EventScheduleTabs } from '@/components/public/EventScheduleTabs';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { formatTime } from '@/lib/dates';
import { ArrowLeft, Calendar, MapPin, Clock, Info, Trophy, List, QrCode, Users, X, CheckCircle, AlertCircle, ChevronDown, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import QRCode from 'react-qr-code';
import TeamDetailScreen from './TeamDetailScreen';
import MobileTicketPurchase from './MobileTicketPurchase';
import { toast } from 'sonner';
import { parseLocalDate } from '@/utils/formatters';

interface EventDetailScreenProps {
  eventId: string;
  onBack: () => void;
  userId?: string;
  userEmail?: string;
  profileId?: string;
}

type EventTab = 'schedule' | 'scores' | 'info';

interface TeamRegistration {
  id: string;
  registrant_data: {
    team?: {
      id?: string;
      name?: string;
      division_id?: string;
    };
    coach?: {
      name?: string;
      email?: string;
    };
  };
  status: string;
  qr_code: string | null;
}

interface Match {
  id: string;
  tournament_id: string;
  team1_name: string | null;
  team2_name: string | null;
  team1_score: number | null;
  team2_score: number | null;
  status: string;
  scheduled_time: string | null;
  court_field: string | null;
  tournament_name?: string;
  round_number?: number | null;
  match_number?: number | null;
  bracket_type?: string | null;
  bracket_label?: string | null;
  venue_name?: string | null;
}

interface Tournament {
  id: string;
  name: string;
}

const EventDetailScreen: React.FC<EventDetailScreenProps> = ({ eventId, onBack, userId, userEmail, profileId }) => {
  const [activeTab, setActiveTab] = useState<EventTab>('schedule');
  const [showEventPassQR, setShowEventPassQR] = useState(false);
  const [selectedTeamQR, setSelectedTeamQR] = useState<{ teamName: string; qrCode: string } | null>(null);
  const [showTicketPurchase, setShowTicketPurchase] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamRegistration | null>(null);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event-detail', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch user's teams for this event
  const { data: myTeams } = useQuery({
    queryKey: ['my-teams', eventId, userEmail],
    queryFn: async () => {
      if (!userEmail) return [];
      
      const { data: registrations, error } = await supabase
        .from('registrations')
        .select('id, registrant_data, status, qr_code')
        .eq('event_id', eventId)
        .filter('registrant_data->coach->>email', 'eq', userEmail);

      if (error) {
        console.error('[EventDetail] Error fetching teams:', error);
        return [];
      }
      
      return (registrations || []) as TeamRegistration[];
    },
    enabled: !!userEmail,
  });

  // Fetch spectator tickets for this event
  const { data: spectatorTickets } = useQuery({
    queryKey: ['spectator-tickets', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_ticket_options')
        .select('id, name, price, is_active')
        .eq('event_id', eventId)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    },
  });

  // Published schedule from rle-public-schedule — the same public service the
  // website uses. NOT_PUBLISHED (404) falls through to "No schedule available yet".
  const { data: publicSchedule } = useQuery({
    queryKey: ['public-schedule-mobile', event?.slug],
    enabled: !!event?.slug,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('rle-public-schedule', {
        body: { action: 'get', slug: event.slug },
      });
      // Response is { ok, data } or { ok:false, error }.
      if (error || !data?.ok) return null;
      return data.data;
    },
  });

  // Brackets use their dedicated public payload because it resolves pool-place
  // and winner/loser sources to team names as soon as those teams are known.
  const { data: publicBrackets } = useQuery({
    queryKey: ['public-brackets-mobile', event?.slug],
    enabled: !!event?.slug,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('rle-public-schedule', {
        body: { action: 'brackets', slug: event.slug },
      });
      if (error || !data?.ok) return null;
      return data.data;
    },
  });

  const { data: publicVenues } = useQuery({
    queryKey: ['public-venues-mobile', event?.slug],
    enabled: !!event?.slug,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('rle-public-schedule', {
        body: { action: 'venues', slug: event.slug },
      });
      if (error || !data?.ok) return null;
      return data.data;
    },
  });

  const bracketGamesById = new Map(
    (publicBrackets?.divisions || []).flatMap((division: any) =>
      (division.rounds || []).flatMap((round: any) =>
        (round.games || []).map((game: any) => [game.id, game])
      )
    )
  );
  const venueForCourt = (court: string | null) =>
    publicVenues?.venues?.find((venue: any) => venue.courts?.includes(court))?.name
      || event?.venue_name || null;

  const bracketDivisions = publicBrackets?.all_divisions || [];
  const tournaments: Tournament[] = bracketDivisions.map((d: string) => ({ id: d, name: d }));

  const matches: Match[] = (publicSchedule?.days || []).flatMap((day: any) =>
    (day.games || []).map((g: any) => ({
      id: g.id,
      tournament_id: g.division || '',
      tournament_name: g.division || undefined,
      team1_name: g.team_a || null,
      team2_name: g.team_b || null,
      team1_score: g.score_a ?? null,
      team2_score: g.score_b ?? null,
      status: g.status,
      // Local wall-clock time (no Z) so the event's day/time render unshifted.
      scheduled_time: day.date && g.start ? `${day.date}T${g.start.slice(0, 5)}:00` : null,
      court_field: g.court || null,
      round_number: g.round_number ?? null,
      match_number: g.game_number ?? null,
      bracket_type: g.game_type ?? null,
      bracket_label: bracketGamesById.get(g.id)?.label || g.bracket_label || null,
      venue_name: venueForCourt(g.court),
    }))
  );

  const bracketMatches: Match[] = (publicBrackets?.divisions || []).flatMap((division: any) =>
    (division.rounds || []).flatMap((round: any) =>
      (round.games || []).map((g: any) => ({
        id: g.id,
        tournament_id: division.division || '',
        tournament_name: division.division || undefined,
        team1_name: g.team_a || null,
        team2_name: g.team_b || null,
        team1_score: g.score_a ?? null,
        team2_score: g.score_b ?? null,
        status: g.status,
        scheduled_time: g.date && g.start ? `${g.date}T${g.start.slice(0, 5)}:00` : null,
        court_field: g.court || null,
        round_number: g.round ?? g.round_number ?? null,
        match_number: g.game_number ?? g.slot ?? null,
        bracket_type: g.group || g.game_type || null,
        bracket_label: g.label || g.bracket_label || null,
        venue_name: venueForCourt(g.court),
      }))
    )
  );

  const tabs = [
    { id: 'schedule' as EventTab, label: 'Schedule', icon: Clock },
    { id: 'scores' as EventTab, label: 'Brackets', icon: Trophy },
    { id: 'info' as EventTab, label: 'Info', icon: Info },
  ];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      confirmed: { bg: 'bg-green-500/10', text: 'text-green-600', icon: <CheckCircle className="h-3 w-3" /> },
      pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', icon: <AlertCircle className="h-3 w-3" /> },
      waitlisted: { bg: 'bg-blue-500/10', text: 'text-blue-600', icon: <AlertCircle className="h-3 w-3" /> },
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', style.bg, style.text)}>
        {style.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Generate event pass QR value - using PASS prefix as requested
  const eventPassQRValue = profileId ? `PASS-${eventId}-${profileId}` : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <p className="text-muted-foreground">Event not found</p>
        <button onClick={onBack} className="text-primary mt-4">Go back</button>
      </div>
    );
  }

  // If showing ticket purchase screen
  if (showTicketPurchase && profileId) {
    return (
      <MobileTicketPurchase
        eventId={eventId}
        eventName={event.name}
        profileId={profileId}
        onBack={() => setShowTicketPurchase(false)}
        onPurchaseComplete={() => setShowTicketPurchase(false)}
      />
    );
  }

  // If a team is selected, show TeamDetailScreen
  if (selectedTeam) {
    const teamName = selectedTeam.registrant_data?.team?.name || 'Unnamed Team';
    const division = selectedTeam.registrant_data?.team?.division_id;
    const coachInfo = selectedTeam.registrant_data?.coach;

    return (
      <TeamDetailScreen
        registrationId={selectedTeam.id}
        teamId={selectedTeam.registrant_data?.team?.id}
        teamName={teamName}
        division={division}
        status={selectedTeam.status}
        coachInfo={coachInfo}
        onBack={() => setSelectedTeam(null)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Event Pass QR Modal */}
      {showEventPassQR && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-6">
          <button 
            onClick={() => setShowEventPassQR(false)}
            className="absolute top-4 right-4 text-white p-2"
          >
            <X className="h-8 w-8" />
          </button>
          <h2 className="text-white text-xl font-bold mb-2">My Event Pass</h2>
          <p className="text-white/70 text-sm mb-6">{event.name}</p>
          <div className="bg-white p-6 rounded-2xl">
            <QRCode value={eventPassQRValue} size={250} />
          </div>
          <p className="text-white/50 text-xs mt-4">Scan for quick identification</p>
        </div>
      )}

      {/* Team QR Modal */}
      {selectedTeamQR && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-6">
          <button 
            onClick={() => setSelectedTeamQR(null)}
            className="absolute top-4 right-4 text-white p-2"
          >
            <X className="h-8 w-8" />
          </button>
          <h2 className="text-white text-xl font-bold mb-2">Team Check-in</h2>
          <p className="text-white/70 text-sm mb-6">{selectedTeamQR.teamName}</p>
          <div className="bg-white p-6 rounded-2xl">
            <QRCode value={selectedTeamQR.qrCode} size={250} />
          </div>
          <p className="text-white/50 text-xs mt-4">Present this code at check-in</p>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="p-1">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold truncate flex-1">{event.name}</h1>
        </div>

        {/* Event Info Bar */}
        <div className="px-4 pb-3 flex flex-wrap gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>
              {format(parseLocalDate(event.start_date), 'MMM d')}
              {event.end_date && event.end_date !== event.start_date && (
                <> - {format(parseLocalDate(event.end_date), 'MMM d')}</>
              )}
            </span>
          </div>
          {(event.venue_city || event.venue_state) && (
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span>{[event.venue_city, event.venue_state].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>

        {/* Event Pass Button */}
        {profileId && (
          <div className="px-4 pb-3">
            <button
              onClick={() => setShowEventPassQR(true)}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-medium"
            >
              <QrCode className="h-5 w-5" />
              My Event Pass
            </button>
          </div>
        )}

        {/* Buy Tickets Button */}
        {spectatorTickets && spectatorTickets.length > 0 && (
          <div className="px-4 pb-3">
            <button
              onClick={() => {
                if (profileId) {
                  setShowTicketPurchase(true);
                } else {
                  // Could show login prompt here
                  toast.info('Please sign in to purchase tickets');
                }
              }}
              className="w-full flex items-center justify-center gap-2 bg-secondary text-secondary-foreground py-2.5 rounded-lg font-medium border border-border"
            >
              <Ticket className="h-5 w-5" />
              Buy Spectator Tickets
            </button>
          </div>
        )}

        {/* My Teams Section */}
        {myTeams && myTeams.length > 0 && (
          <div className="px-4 pb-3">
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Users className="h-4 w-4" />
              My Teams ({myTeams.length})
            </h3>
            <div className="space-y-2">
              {myTeams.map((team) => {
                const teamName = team.registrant_data?.team?.name || 'Unnamed Team';
                const division = team.registrant_data?.team?.division_id || '';
                
                return (
                  <div 
                    key={team.id} 
                    className="bg-card border border-border rounded-lg p-3 cursor-pointer active:bg-muted/50 transition-colors"
                    onClick={() => setSelectedTeam(team)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{teamName}</p>
                        {division && (
                          <p className="text-xs text-muted-foreground">{division}</p>
                        )}
                        <div className="mt-1">
                          {getStatusBadge(team.status)}
                        </div>
                      </div>
                      {team.qr_code && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTeamQR({ teamName, qrCode: team.qr_code! });
                          }}
                          className="flex items-center gap-1 bg-muted px-3 py-1.5 rounded-md text-sm font-medium"
                        >
                          <QrCode className="h-4 w-4" />
                          Check-in
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-t border-border">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors border-b-2",
                  isActive
                    ? "text-primary border-primary"
                    : "text-muted-foreground border-transparent hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </header>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'schedule' && (
          <ScheduleTab matches={matches || []} eventId={event.id} />
        )}
        {activeTab === 'scores' && (
          <ScoresTab matches={bracketMatches} tournaments={tournaments} />
        )}
        {activeTab === 'info' && (
          <InfoTab event={event} />
        )}
      </div>
    </div>
  );
};

// Schedule Tab - Shows matches grouped by day with date filter
const ScheduleTab: React.FC<{ matches: Match[]; eventId: string }> = ({ matches, eventId }) => {
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Get unique dates from matches
  const uniqueDates = React.useMemo(() => {
    const dates = new Set<string>();
    matches.forEach(match => {
      if (match.scheduled_time) {
        try {
          const dateKey = format(new Date(match.scheduled_time), 'yyyy-MM-dd');
          dates.add(dateKey);
        } catch {
          // Skip invalid dates
        }
      }
    });
    return Array.from(dates).sort();
  }, [matches]);

  // Format date for display
  const formatDateDisplay = (dateStr: string) => {
    try {
      return format(parseLocalDate(dateStr), 'EEEE, MMMM d');
    } catch {
      return dateStr;
    }
  };

  // Filter matches by selected date
  const filteredMatches = React.useMemo(() => {
    if (selectedDate === 'all') return matches;
    return matches.filter(match => {
      if (!match.scheduled_time) return false;
      try {
        const matchDate = format(new Date(match.scheduled_time), 'yyyy-MM-dd');
        return matchDate === selectedDate;
      } catch {
        return false;
      }
    });
  }, [matches, selectedDate]);

  // Group matches by day
  const matchesByDay = filteredMatches.reduce((acc, match) => {
    let dayKey = 'TBD';
    if (match.scheduled_time) {
      try {
        dayKey = format(new Date(match.scheduled_time), 'EEEE, MMMM d');
      } catch {
        dayKey = 'TBD';
      }
    }
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(match);
    return acc;
  }, {} as Record<string, Match[]>);

  if (!matches.length) {
    return (
      <div>
      <div className="px-4"><EventScheduleTabs eventId={eventId} /></div>
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <Clock className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground text-center">No schedule available yet</p>
      </div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="px-4"><EventScheduleTabs eventId={eventId} /></div>
      {/* Date Picker - show if there are any dates */}
      {uniqueDates.length > 0 && (
        <div className="p-4 border-b border-border">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full flex items-center justify-between px-3 py-2 bg-muted rounded-lg text-left"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">
                {selectedDate === 'all' ? 'All Days' : formatDateDisplay(selectedDate)}
              </span>
            </div>
            <ChevronDown className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              showDatePicker && "rotate-180"
            )} />
          </button>
          
          {showDatePicker && (
            <div className="mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
              <button
                onClick={() => {
                  setSelectedDate('all');
                  setShowDatePicker(false);
                }}
                className={cn(
                  "w-full px-3 py-2.5 text-left hover:bg-muted transition-colors flex items-center gap-2",
                  selectedDate === 'all' && "bg-primary/10 text-primary"
                )}
              >
                <Calendar className="h-4 w-4" />
                All Days
              </button>
              {uniqueDates.map((dateStr) => (
                <button
                  key={dateStr}
                  onClick={() => {
                    setSelectedDate(dateStr);
                    setShowDatePicker(false);
                  }}
                  className={cn(
                    "w-full px-3 py-2.5 text-left hover:bg-muted transition-colors",
                    selectedDate === dateStr && "bg-primary/10 text-primary"
                  )}
                >
                  {formatDateDisplay(dateStr)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Matches grouped by day */}
      {Object.entries(matchesByDay).map(([day, dayMatches]) => (
        <div key={day}>
          <div className="sticky top-0 bg-muted/80 backdrop-blur-sm px-4 py-2 border-b border-border">
            <span className="text-sm font-medium">{day}</span>
          </div>
          <div className="divide-y divide-border">
            {dayMatches.map((match) => (
              <div key={match.id} className="p-4 bg-card">
                {(match.bracket_label || match.bracket_type === 'bracket' || match.bracket_type === 'consolation') && (
                  <h3 className="font-semibold mb-2 break-words">
                    {[match.tournament_name?.toUpperCase(), match.bracket_label || (match.match_number ? `Bracket Game ${match.match_number}` : 'Bracket Game')].filter(Boolean).join(' ')}
                  </h3>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    {match.scheduled_time 
                      ? formatTime(match.scheduled_time)
                      : 'TBD'}
                  </span>
                  <div className="flex items-center gap-2">
                    {match.court_field && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">{match.court_field}</span>
                    )}
                    {match.status === 'in_progress' && (
                      <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        LIVE
                      </span>
                    )}
                    {match.status === 'completed' && (
                      <span className="text-xs text-muted-foreground font-medium">Final</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium">{match.team1_name || 'TBD'}</p>
                  </div>
                  <div className="px-4 text-center">
                    {match.status === 'completed' || match.status === 'in_progress' ? (
                      <span className="text-lg font-bold">
                        {match.team1_score ?? 0} - {match.team2_score ?? 0}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">vs</span>
                    )}
                  </div>
                  <div className="flex-1 text-right">
                    <p className="font-medium">{match.team2_name || 'TBD'}</p>
                  </div>
                </div>
                {(match.bracket_label || match.bracket_type === 'bracket' || match.bracket_type === 'consolation') && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {[match.court_field, formatTime(match.scheduled_time)].filter(Boolean).join(' · ')}
                  </p>
                )}
                {match.tournament_name && !match.bracket_label && match.bracket_type !== 'bracket' && match.bracket_type !== 'consolation' && (
                  <p className="text-xs text-muted-foreground mt-2">{match.tournament_name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Brackets Tab - Shows tournament brackets organized by round
const ScoresTab: React.FC<{ matches: Match[]; tournaments: Tournament[] }> = ({ matches, tournaments }) => {
  // Auto-select first tournament
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(
    tournaments.length > 0 ? tournaments[0].id : null
  );
  const [showTournamentPicker, setShowTournamentPicker] = useState(false);

  // Update selected tournament when tournaments load
  React.useEffect(() => {
    if (tournaments.length > 0 && !selectedTournamentId) {
      setSelectedTournamentId(tournaments[0].id);
    }
  }, [tournaments, selectedTournamentId]);

  // Debug logging
  console.log('[Brackets] Tournament ID:', selectedTournamentId);
  console.log('[Brackets] Raw matches:', matches);
  console.log('[Brackets] Tournaments:', tournaments);

  // Filter matches by selected tournament
  const filteredMatches = selectedTournamentId 
    ? matches.filter(m => m.tournament_id === selectedTournamentId)
    : matches;

  console.log('[Brackets] Filtered matches for tournament:', filteredMatches);

  // Sort by round_number and match_number for bracket display
  const sortedMatches = [...filteredMatches].sort((a, b) => {
    const roundA = a.round_number ?? 999;
    const roundB = b.round_number ?? 999;
    if (roundA !== roundB) return roundA - roundB;
    const matchA = a.match_number ?? 999;
    const matchB = b.match_number ?? 999;
    return matchA - matchB;
  });

  // Group matches by bracket_type: pool, winners, final
  const bracketTypes = ['pool', 'winners', 'final'];
  const matchesByBracket: Record<string, Match[]> = {};
  
  // Separate matches by bracket type
  bracketTypes.forEach(type => {
    const bracketMatches = sortedMatches.filter(m => m.bracket_type === type);
    if (bracketMatches.length > 0) {
      matchesByBracket[type] = bracketMatches;
    }
  });

  // Handle matches without bracket_type or with other values (treat as main bracket)
  const otherMatches = sortedMatches.filter(m => !m.bracket_type || !bracketTypes.includes(m.bracket_type));
  if (otherMatches.length > 0) {
    // If we already have categorized matches, add these to a "main" section
    // Otherwise, treat all matches as the main bracket
    if (Object.keys(matchesByBracket).length === 0) {
      matchesByBracket['main'] = otherMatches;
    } else {
      matchesByBracket['other'] = otherMatches;
    }
  }

  // Group by round within each bracket type
  const groupByRound = (matchList: Match[]) => {
    return matchList.reduce((acc, match) => {
      const roundKey = match.round_number ?? 0;
      if (!acc[roundKey]) acc[roundKey] = [];
      acc[roundKey].push(match);
      return acc;
    }, {} as Record<number, Match[]>);
  };

  // Get round name based on round number and total rounds
  const getRoundName = (roundNumber: number, totalRounds: number, bracketType: string) => {
    // For pool play, just show "Pool Play" or game numbers
    if (bracketType === 'pool') {
      return `Game ${roundNumber}`;
    }
    // For finals, show "Championship"
    if (bracketType === 'final') {
      return 'Championship';
    }
    // For winners bracket
    const roundsFromEnd = totalRounds - roundNumber;
    if (roundsFromEnd === 0) return 'Finals';
    if (roundsFromEnd === 1) return 'Semifinals';
    if (roundsFromEnd === 2) return 'Quarterfinals';
    return `Round ${roundNumber}`;
  };

  const getBracketLabel = (type: string) => {
    switch (type) {
      case 'pool': return 'Pool Play';
      case 'winners': return 'Bracket';
      case 'final': return 'Finals';
      case 'main': return 'Bracket';
      case 'other': return 'Other Games';
      default: return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getBracketIcon = (type: string) => {
    switch (type) {
      case 'pool': return '🏐';
      case 'winners': return '🏆';
      case 'final': return '🥇';
      default: return '📋';
    }
  };

  const selectedTournament = tournaments.find(t => t.id === selectedTournamentId);

  if (!matches.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <Trophy className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-muted-foreground text-center">No bracket available yet</p>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Tournament Picker (if multiple tournaments) */}
      {tournaments.length > 1 && (
        <div className="p-4 border-b border-border">
          <button
            onClick={() => setShowTournamentPicker(!showTournamentPicker)}
            className="w-full flex items-center justify-between px-3 py-2 bg-muted rounded-lg text-left"
          >
            <span className="font-medium truncate">
              {selectedTournament?.name || 'Select Tournament'}
            </span>
            <ChevronDown className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              showTournamentPicker && "rotate-180"
            )} />
          </button>
          
          {showTournamentPicker && (
            <div className="mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
              {tournaments.map((tournament) => (
                <button
                  key={tournament.id}
                  onClick={() => {
                    setSelectedTournamentId(tournament.id);
                    setShowTournamentPicker(false);
                  }}
                  className={cn(
                    "w-full px-3 py-2.5 text-left hover:bg-muted transition-colors",
                    selectedTournamentId === tournament.id && "bg-primary/10 text-primary"
                  )}
                >
                  {tournament.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Show message if no matches found for selected tournament */}
      {selectedTournamentId && filteredMatches.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <Trophy className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-center">No bracket matches yet</p>
          <p className="text-xs text-muted-foreground mt-1">Check back when the bracket is published</p>
        </div>
      )}

      {/* Bracket Display */}
      {selectedTournamentId && filteredMatches.length > 0 && Object.entries(matchesByBracket).map(([bracketType, bracketMatches]) => {
        const roundsData = groupByRound(bracketMatches);
        const rounds = Object.keys(roundsData).map(Number).sort((a, b) => a - b);
        const totalRounds = Math.max(...rounds);

        return (
          <div key={bracketType} className="mb-6">
            {/* Bracket Type Header */}
            <div className="px-4 py-3 bg-primary/10 border-b border-primary/20 flex items-center gap-2">
              <span className="text-lg">{getBracketIcon(bracketType)}</span>
              <span className="text-sm font-semibold text-primary">{getBracketLabel(bracketType)}</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {bracketMatches.length} {bracketMatches.length === 1 ? 'match' : 'matches'}
              </span>
            </div>

            {/* Rounds */}
            {rounds.map((roundNumber) => {
              const roundMatches = roundsData[roundNumber];
              const roundName = getRoundName(roundNumber, totalRounds, bracketType);
              const isFinalsRound = bracketType === 'final' || roundNumber === totalRounds;

              return (
                <div key={roundNumber} className="mb-4">
                  <div className={cn(
                    "px-4 py-2 border-b border-border",
                    isFinalsRound ? "bg-yellow-500/10" : "bg-muted/50"
                  )}>
                    <span className={cn(
                      "text-sm font-medium",
                      isFinalsRound ? "text-yellow-600" : "text-muted-foreground"
                    )}>
                      {roundName}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({roundMatches.length} {roundMatches.length === 1 ? 'match' : 'matches'})
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {roundMatches.map((match) => (
                      <BracketMatchCard key={match.id} match={match} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// Bracket Match Card component
const BracketMatchCard: React.FC<{ match: Match }> = ({ match }) => {
  const isLive = match.status === 'in_progress';
  const isCompleted = match.status === 'completed';
  const team1Won = isCompleted && (match.team1_score ?? 0) > (match.team2_score ?? 0);
  const team2Won = isCompleted && (match.team2_score ?? 0) > (match.team1_score ?? 0);

  return (
    <div className={cn(
      "p-4 bg-card",
      isLive && "bg-red-500/5 border-l-2 border-l-red-500"
    )}>
      {/* Match info */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">
          {match.bracket_label || (match.match_number ? `Match ${match.match_number}` : 'Bracket Game')}
        </span>
        {isLive && (
          <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            LIVE
          </span>
        )}
        {isCompleted && (
          <span className="text-xs text-muted-foreground font-medium">Final</span>
        )}
      </div>

      <div className="space-y-1 mb-3 text-xs text-muted-foreground">
        {match.scheduled_time && (
          <p className="flex items-start gap-1.5">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>{format(parseLocalDate(match.scheduled_time.split('T')[0]), 'EEE, MMM d')} · {formatTime(match.scheduled_time)}</span>
          </p>
        )}
        {(match.venue_name || match.court_field) && (
          <p className="flex items-start gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="break-words">{[match.venue_name, match.court_field].filter(Boolean).join(' · ')}</span>
          </p>
        )}
      </div>

      {/* Teams */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className={cn(
            "flex-1 font-medium truncate",
            team1Won && "text-primary"
          )}>
            {match.team1_name || 'TBD'}
          </span>
          <span className={cn(
            "text-xl font-bold tabular-nums min-w-[2rem] text-right",
            team1Won && "text-primary",
            !isLive && !isCompleted && "text-muted-foreground text-base"
          )}>
            {isLive || isCompleted ? (match.team1_score ?? 0) : '-'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={cn(
            "flex-1 font-medium truncate",
            team2Won && "text-primary"
          )}>
            {match.team2_name || 'TBD'}
          </span>
          <span className={cn(
            "text-xl font-bold tabular-nums min-w-[2rem] text-right",
            team2Won && "text-primary",
            !isLive && !isCompleted && "text-muted-foreground text-base"
          )}>
            {isLive || isCompleted ? (match.team2_score ?? 0) : '-'}
          </span>
        </div>
      </div>
    </div>
  );
};

// (ScoreMatchCard removed - replaced by BracketMatchCard)

// Info Tab - Renders HTML content properly for all fields
const InfoTab: React.FC<{ event: any }> = ({ event }) => {
  // Helper to render HTML content safely
  const renderHtml = (html: string | null) => {
    if (!html) return null;
    return (
      <div 
        className="prose prose-sm max-w-none text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: html }} 
      />
    );
  };

  return (
    <div className="p-4 space-y-6">
      {/* Venue */}
      <div>
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Venue
        </h3>
        <div className="bg-card rounded-lg border border-border p-4">
          {event.venue_name && <p className="font-medium">{event.venue_name}</p>}
          {event.venue_address && <p className="text-sm text-muted-foreground">{event.venue_address}</p>}
          <p className="text-sm text-muted-foreground">
            {[event.venue_city, event.venue_state, event.venue_zip].filter(Boolean).join(', ')}
          </p>
        </div>
      </div>

      {/* About/Description */}
      {event.description && (
        <div>
          <h3 className="font-medium mb-2">About</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {renderHtml(event.description)}
          </div>
        </div>
      )}

      {/* Event Format */}
      {event.event_format && (
        <div>
          <h3 className="font-medium mb-2">Event Format</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {renderHtml(event.event_format)}
          </div>
        </div>
      )}

      {/* Pricing */}
      {event.pricing_notes && (
        <div>
          <h3 className="font-medium mb-2">Pricing</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {renderHtml(event.pricing_notes)}
          </div>
        </div>
      )}

      {/* Rules */}
      {event.rules && (
        <div>
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <List className="h-4 w-4" />
            Rules
          </h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {renderHtml(event.rules)}
          </div>
        </div>
      )}

      {/* Check-in Instructions */}
      {event.check_in_instructions_html && (
        <div>
          <h3 className="font-medium mb-2">Check-in Instructions</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {renderHtml(event.check_in_instructions_html)}
          </div>
        </div>
      )}

      {/* Hotel Booking */}
      {event.hotel_booking_url && (
        <div>
          <h3 className="font-medium mb-2">Hotel Booking</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            <a 
              href={event.hotel_booking_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Book Hotel Room
            </a>
          </div>
        </div>
      )}

      {/* Live Streaming */}
      {event.live_streaming_url && (
        <div>
          <h3 className="font-medium mb-2">Live Streaming</h3>
          <div className="bg-card rounded-lg border border-border p-4">
            {event.live_streaming_description && renderHtml(event.live_streaming_description)}
            <a 
              href={event.live_streaming_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Watch Live
            </a>
          </div>
        </div>
      )}

      {/* Contact */}
      {(event.contact_email || event.contact_phone) && (
        <div>
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Info className="h-4 w-4" />
            Contact
          </h3>
          <div className="bg-card rounded-lg border border-border p-4 space-y-1">
            {event.contact_email && (
              <p className="text-sm">
                <span className="text-muted-foreground">Email:</span>{' '}
                <a href={`mailto:${event.contact_email}`} className="text-primary">
                  {event.contact_email}
                </a>
              </p>
            )}
            {event.contact_phone && (
              <p className="text-sm">
                <span className="text-muted-foreground">Phone:</span>{' '}
                <a href={`tel:${event.contact_phone}`} className="text-primary">
                  {event.contact_phone}
                </a>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailScreen;
